import { Injectable, OnDestroy, NgZone } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { Utenteservice } from './utente.service';
import { Utente } from '../class/userinterface';
import { v1 as uuidv1 } from 'uuid';
import { environment } from '../../environments/environment';

export type StatusType = "Ringing" | "Dialing" | "Connected";
export type WsStatus = 'CONNECTING' | 'CONNECTED' | 'DISCONNECTED' | 'ERROR';
export type TypeSource = "Wroutepoint" | "Wextension" | "Wexternalline";

export interface WSResponse {
  internal: string;
  caller: string;
  status?: StatusType;
  nome: string;
  azienda: string;
  msg: WSResponse3cx;
}

export interface WSResponse3cx {
  dn: string;
  type: TypeSource;
  devices: Array<{
    dn: string;
    device_id: string;
    user_agent: string;
  }>;
  participants: Array<{
    id: number;
    status: StatusType;
    party_caller_name: string;
    party_dn: string;
    party_caller_id: string;
    device_id: string;
    party_dn_type: TypeSource;
    direct_control: boolean;
    callid: number;
    legid: number;
    dn: string;
  }>;
}

export interface ToastCall {
  status: StatusType | 'Disconnected';
  data: WSResponse3cx;
  id: string;
}

@Injectable({ providedIn: 'root' })
export class TreCxService implements OnDestroy {
  private socket: WebSocket | null = null;

  private message$ = new Subject<WSResponse3cx>();

  // ✅ stato “vero” e sempre disponibile
  private status$ = new BehaviorSubject<WsStatus>('DISCONNECTED');

  private utenteConnesso: Utente;
  private reconnectTimer: any;
  private counter = 0;

  // ✅ evita reconnect quando chiudi tu (disconnect manuale)
  private shouldReconnect = true;

  private _toasts = new BehaviorSubject<ToastCall | null>(null);
  public toasts$ = this._toasts.asObservable();

  private readonly RECONNECT_DELAY = 5000;  // base
  private readonly MAX_RECONNECT = 300;

  constructor(
    private readonly utenteService: Utenteservice,
    private readonly zone: NgZone
  ) { }

  /* ================= CONNECT ================= */
  connect(): void {
    this.shouldReconnect = true;

    this.utenteConnesso = this.utenteService.InfoUtenteConnesso;

    // già connesso o in connessione -> non duplicare
    if (this.socket && (this.socket.readyState === WebSocket.OPEN || this.socket.readyState === WebSocket.CONNECTING)) {
      return;
    }

    if (!this.utenteConnesso || !this.utenteConnesso.telefono || this.utenteConnesso.telefono.length > 3) return;
    if (this.counter > this.MAX_RECONNECT) return;

    // pulisci eventuale timer di reconnect pendente
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    this.status$.next('CONNECTING');

    try {
      this.socket = new WebSocket(
        `${environment.WS_ENDPOINT_3CX}?dn=${this.utenteConnesso.telefono}&name=${this.utenteConnesso.userName}`
      );

      this.socket.onopen = () => {
        this.zone.run(() => {
          console.log('🟢 WS connected');
          this.counter = 0;
          this.status$.next('CONNECTED');
        });
      };

      this.socket.onmessage = (event: MessageEvent) => {
        this.zone.run(() => {
          try {
            const data: WSResponse = JSON.parse(event.data);

            this.message$.next(data.msg);

            const guid = uuidv1();
            // NB: qui mantengo la tua logica intatta
            this.addToast({
              id: guid,
              status: data.status ? data.status : 'Disconnected',
              data: data.msg.participants.length > 0 ? data.msg : (this._toasts.value ? this._toasts.value.data : data.msg)
            });
          } catch {
            console.log('WS event', event);
            // se vuoi: this.message$.next(event.data) ma il tipo è WSResponse3cx
          }
        });
      };

      this.socket.onerror = () => {
        this.zone.run(() => {
          console.log('WS ERROR');
          this.status$.next('ERROR');
          this.counter++;

          // 🔁 molti browser non chiamano onclose “subito” dopo onerror:
          // forziamo la chiusura per attivare onclose + scheduleReconnect
          try {
            this.socket && this.socket.close();
          } catch { }

          // fallback: se non arriva onclose, schedula comunque
          this.scheduleReconnect();
        });
      };

      this.socket.onclose = () => {
        this.zone.run(() => {
          console.log('WS CLOSE');
          this.status$.next('DISCONNECTED');

          // incrementa (opzionale ma utile per backoff)
          this.counter++;

          this.socket = null;
          this.scheduleReconnect();
        });
      };
    } catch {
      this.counter++;
      this.status$.next('DISCONNECTED');
      this.scheduleReconnect();
    }
  }

  /* ================= STREAMS ================= */
  onMessage(): Observable<WSResponse3cx> {
    return this.message$.asObservable();
  }

  onStatus(): Observable<WsStatus> {
    return this.status$.asObservable();
  }

  getStatusSnapshot(): WsStatus {
    return this.status$.value;
  }

  /* ================= CLOSE ================= */
  disconnect(): void {
    // ❌ chiusura volontaria: non riconnettere
    this.shouldReconnect = false;

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }

    this.status$.next('DISCONNECTED');
    setTimeout(() => { this.connect(); }, 5000)
  }

  // ✅ per il pulsante “Riconnetti”
  reconnect(): void {
    this.counter = 0;
    this.disconnect();
    this.shouldReconnect = true;
  }

  ngOnDestroy(): void {
    this.disconnect();
    this.message$.complete();
    this.status$.complete();
  }

  /* ================= TOAST CALL ================= */
  private addToast(data: ToastCall) {
    this._toasts.next(data);
  }

  public removeToast() {
    this._toasts.next(null);
  }

  /* ================= RECONNECT ================= */
  private scheduleReconnect(): void {
    if (!this.shouldReconnect) return;
    if (this.reconnectTimer) return;
    if (this.counter > this.MAX_RECONNECT) return;

    // backoff semplice: cresce col counter, cap a 60s
    const delay = Math.min(this.RECONNECT_DELAY * Math.max(1, this.counter), 60000);

    this.reconnectTimer = setTimeout(() => {
      console.log('🔄 WS reconnecting...');
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }
}
